import { AuditDTO } from 'src/app/core/models/system/audit/audit.dto';
import { AuditService } from 'src/app/core/services/utelize/system/audit/audit-service';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { CompaniesService } from 'src/app/core/services/utelize/system/company/companies-service';
import { CompanyDTO } from 'src/app/core/models/system/company/company.dto';
import { CompanyUsageLimitDTO } from 'src/app/core/models/system/company/company-usage-limit.dto';
import { CompanyUsageLimitsService } from 'src/app/core/services/utelize/system/company/company-usage-limits-service';
import { Component, EventEmitter, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { DevicesService } from 'src/app/core/services/utelize/system/device/devices-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { IdAndDescriptionDTO } from 'src/app/core/interfaces/general/id-description';
import { LimitType, LimitTypeName, ThrottleType } from 'src/app/core/enums';
import { msSize } from 'src/app/core/consts';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription, switchMap, throwError } from 'rxjs';
import { OrderDTO } from 'src/app/core/models/system/order/order.dto';
import { OrdersService } from 'src/app/core/services/utelize/system/order/orders-service';
import { ProductsService } from 'src/app/core/services/utelize/system/product/products-service';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';
import { WebbingService } from 'src/app/core/services/utelize/system/webbing/webbing-service';

export enum BulkSimAction {
  ChangeProduct        =1,
  Suspend              =2,
  Cease                =3,
  Throttle             =4,
  OverrideLimit        =5,
  ChangeThisMonthLimit =6,
}

export interface BulkSimActionResult {
  companyName: string;
  iccid      : string;
  message    : string;
  ok         : boolean;
  userName   : string;
}

export interface BulkSimRow {
  companyId  : number;
  companyName: string;
  iccid      : string;
  limitType  : number;
  userName   : string;
}

/**
 * Wizard that applies a single SIM operation (change product / suspend / cease / throttle /
 * override limit / change this month's limit). Steps: Configure -> Confirm -> Progress -> Complete.
 */
@Component({
  selector: 'app-bulk-sim-actions-modal-form',
  templateUrl: './bulk-sim-actions-modal-form.component.html',
  styleUrls: ['./bulk-sim-actions-modal-form.component.scss']
})

export class BulkSimActionsModalFormComponent implements OnDestroy {

  @Output() completed: EventEmitter<void>=new EventEmitter<void>();

  @ViewChild('bulkSimActionsModalForm', { static: false }) bulkSimActionsModalForm!: TemplateRef<void>;

  get modalBackGround(): boolean | "static" { return "static"; }

  private currentIndex : number=0;
  private modalRef     : NgbModalRef | undefined;
  private processingSub: Subscription | undefined;

  public action                   : BulkSimAction=BulkSimAction.Suspend;
  public BulkSimAction             =BulkSimAction;
  public companyCurrencySymbol    : string="£";
  public companyDataLimit         : number | string="";
  public companyDataWeightCode    : string="MB";
  public companyUsageLimit        : number | string="";
  public companyUsageLimits       : CompanyUsageLimitDTO | undefined;
  public confirmSummary           : string="";
  public curUser                  : SystemUserDTO;
  public dataWeightCode           : string="MB";
  public errorCount               : number=0;
  public formSubmitted            : boolean=false;
  public LimitType                 =LimitType;
  public limitValue               : number | null=null;
  public monthDataValue           : number | null=null;
  public monthDataWeightCode      : string="MB";
  public monthSpendValue          : number | null=null;
  public overrideDataCurrentMonth : boolean=true;
  public overrideSpendCurrentMonth: boolean=true;
  public processing               : boolean=false;
  public products                 : IdAndDescriptionDTO[]=[];
  public progressPercent          : number=0;
  public results                  : BulkSimActionResult[]=[];
  public runRows                  : BulkSimRow[]=[];
  public selectedLimitType        : number=LimitType.Spend;
  public selectedProductId        : number | null=null;
  public selectedRows             : BulkSimRow[]=[];
  public selectedThrottleId       : number=ThrottleType.Throttle64k;
  public step                     : string="confirm";
  public successCount             : number=0;
  public throttleTypes=[
    { id: ThrottleType.Throttle64k,  description: "64 kbps"  },
    { id: ThrottleType.Throttle512k, description: "512 kbps" },
  ];
  public totalCount         : number=0;
  public useCustomDataLimit : boolean=true;
  public useCustomSpendLimit: boolean=true;

  constructor(
    private auditService             : AuditService,
    private authenticationService    : AuthenticationService,
    private companiesService         : CompaniesService,
    private companyUsageLimitsService: CompanyUsageLimitsService,
    private devicesService           : DevicesService,
    private exceptionsService        : ExceptionsService,
    private modalService             : NgbModal,
    private ordersService            : OrdersService,
    private productsService          : ProductsService,
    private webbingService           : WebbingService
  ) {
    this.curUser=this.authenticationService.currentUserObject();
  }

  public ngOnDestroy(): void {
    try {
      this.processingSub?.unsubscribe();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Getters

  public get currentIndexDisplay(): number {
    try {
      return Math.min(this.currentIndex + 1, this.totalCount);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return this.totalCount;
    }
  }

  public get actionLabel(): string {
    try {
      switch(this.action) {
        case BulkSimAction.ChangeProduct       : return "Change Product";
        case BulkSimAction.Suspend             : return "Suspend";
        case BulkSimAction.Cease               : return "Cease";
        case BulkSimAction.Throttle            : return "Throttle";
        case BulkSimAction.OverrideLimit       : return "Override Limit";
        case BulkSimAction.ChangeThisMonthLimit: return "Change This Month's Limit";
        default                                : return "";
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public get isLimitAction(): boolean {
    try {
      return this.action===BulkSimAction.OverrideLimit || this.action===BulkSimAction.ChangeThisMonthLimit;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public get isOverrideLimit(): boolean {
    try {
      return this.action===BulkSimAction.OverrideLimit;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  //RBW_20260805 - This Month's Limit respects each SIM's existing limit type; count the selected SIMs by type so we only show/require the relevant box(es)
  public get spendSimCount(): number {
    try {
      return this.selectedRows.filter(x => x.limitType===LimitType.Spend).length;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public get dataSimCount(): number {
    try {
      return this.selectedRows.filter(x => x.limitType===LimitType.Data).length;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public get offSimCount(): number {
    try {
      return this.selectedRows.filter(x => x.limitType!==LimitType.Spend && x.limitType!==LimitType.Data).length;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public get hasSpendSims(): boolean {
    try {
      return this.spendSimCount>0;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public get hasDataSims(): boolean {
    try {
      return this.dataSimCount>0;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public get needsConfigure(): boolean {
    try {
      return this.action===BulkSimAction.ChangeProduct ||
             this.action===BulkSimAction.Throttle      ||
             this.isLimitAction;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  //#endregion

  //#region Private Methods

  private auditBulk(message: string, additionalInfo: string): void {
    try {
      const audit: AuditDTO={
        additionalInfo: additionalInfo,
        fromFrontend  : true,
        isError       : false,
        message       : message,
        userID        : this.curUser.userId,
      }

      this.auditService.post(audit)
      .subscribe({
        next : ()    => { },
        error: error => { this.exceptionsService.handleException(new Error(error)); }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private iccidListOrNone(iccids: string[]): string {
    try {
      return (iccids != null && iccids.length>0) ? iccids.join(", ") : "None";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "None";
    }
  }

  private auditResultsInfo(): string {
    try {
      const completed: string[]=this.results.filter(x => x.ok).map(x => x.iccid);
      const failed   : string[]=this.results.filter(x => !x.ok).map(x => x.iccid);

      return "Completed: " + this.iccidListOrNone(completed) + "\nFailed: " + this.iccidListOrNone(failed);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private buildConfirmSummary(): void {
    try {
      const count=this.totalCount;

      switch(this.action) {
        case BulkSimAction.ChangeProduct:
          const product=this.products.find(x => x.id===this.selectedProductId);
          this.confirmSummary="Change product to <strong>" + (product?.description ?? "") + "</strong> on <strong>" + count + "</strong> connection(s).";
          break;
        case BulkSimAction.Suspend:
          this.confirmSummary="Suspend <strong>" + count + "</strong> connection(s).";
          break;
        case BulkSimAction.Cease:
          this.confirmSummary="Cease (deprovision) <strong>" + count + "</strong> connection(s). This cannot be undone.";
          break;
        case BulkSimAction.Throttle:
          const throttle=this.throttleTypes.find(x => x.id===this.selectedThrottleId);
          this.confirmSummary="Throttle <strong>" + count + "</strong> connection(s) to <strong>" + (throttle?.description ?? "") + "</strong>.";
          break;
        case BulkSimAction.OverrideLimit:
          this.confirmSummary="Set the limit to <strong>" + this.limitDescription() + "</strong> on <strong>" + count + "</strong> connection(s).";
          break;
        case BulkSimAction.ChangeThisMonthLimit:
          const parts: string[]=[];

          if (this.hasSpendSims) {
            parts.push(this.overrideSpendCurrentMonth
                       ? "set this month's spend limit to <strong>" + this.companyCurrencySymbol + this.monthSpendValue + "</strong> on <strong>" + this.spendSimCount + "</strong> connection(s)"
                       : "clear this month's spend limit override on <strong>" + this.spendSimCount + "</strong> connection(s)");
          }

          if (this.hasDataSims) {
            parts.push(this.overrideDataCurrentMonth
                       ? "set this month's data limit to <strong>" + this.monthDataValue + " " + this.monthDataWeightCode + "</strong> on <strong>" + this.dataSimCount + "</strong> connection(s)"
                       : "clear this month's data limit override on <strong>" + this.dataSimCount + "</strong> connection(s)");
          }

          this.confirmSummary="This will " + parts.join(" and ") + ".";

          if (this.offSimCount>0) {
            this.confirmSummary+=" <span class='text-muted'>(" + this.offSimCount + " selected SIM(s) with no limit set will be skipped.)</span>";
          }
          break;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private limitDescription(): string {
    try {
      if (this.selectedLimitType===LimitType.Off) {
        return "No Limit";
      }

      if (this.selectedLimitType===LimitType.Data) {
        if (this.isOverrideLimit && !this.useCustomDataLimit) {
          return "the Company Level data limit";
        }

        return this.limitValue + " " + this.dataWeightCode + " (data)";
      }

      if (this.isOverrideLimit && !this.useCustomSpendLimit) {
        return "the Company Level spend limit";
      }

      return this.companyCurrencySymbol + this.limitValue + " (spend)";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private applyOverrideLimitToOrder(order: OrderDTO): void {
    try {
      const value: number | null=this.limitValue;

      order.limitType                     =this.selectedLimitType;
      order.limitCurrent                  =null;
      order.dataLimitCurrent              =null;
      order.dataLimitCurrentDataWeightCode=null;
      order.limitSelfAuth                 =null;

      if (this.selectedLimitType===LimitType.Off) {
        order.dataLimit              =null;
        order.dataLimitDataWeightCode=null;
        order.limit                  =null;
      } else if (this.selectedLimitType===LimitType.Data) {
        order.dataLimit              =this.useCustomDataLimit ? value : null;
        order.dataLimitDataWeightCode=this.useCustomDataLimit ? this.dataWeightCode : null;
        order.limit                  =null;
      } else {
        order.limit                  =this.useCustomSpendLimit ? value : null;
        order.dataLimit              =null;
        order.dataLimitDataWeightCode=null;
      }

      if (this.companyUsageLimits != undefined) {
        let keepLimitType: boolean=false;

        if (this.selectedLimitType===LimitType.Off) {
          keepLimitType=(this.companyUsageLimits.limitType != LimitType.Off);
        } else if (this.selectedLimitType===LimitType.Spend) {
          keepLimitType=(order.limit != null && order.limit != this.companyUsageLimits.limit);
        } else if (this.selectedLimitType===LimitType.Data) {
          keepLimitType=(order.dataLimit != null && order.dataLimit != this.companyUsageLimits.dataLimit);
        }

        if (order.dataLimit == this.companyUsageLimits.dataLimit) order.dataLimit =null;
        if (order.limit     == this.companyUsageLimits.limit)     order.limit     =null;

        if (!keepLimitType) order.limitType =null;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private executeForRow(row: BulkSimRow): Observable<any> {
    try {
      const iccid : string=row?.iccid ?? "";
      const userId: number=<number>this.curUser.userId;

      if (iccid.trim()==="") {
        return throwError(() => new Error("BulkSimActionsModalFormComponent.executeForRow() - ICCID could not be determined for the selected connection"));
      }

      switch(this.action) {
        case BulkSimAction.ChangeProduct:
          return this.webbingService.SetProduct(iccid, <number>this.selectedProductId)
                 .pipe(
                   switchMap(() => this.ordersService.getOrderIdByIccid(iccid)),
                   switchMap(idResult => {
                     const orderId: number=<number>(idResult?.result ?? -1);

                     if (orderId<=0) {
                       return throwError(() => new Error("BulkSimActionsModalFormComponent.executeForRow() - Order could not be found for ICCID"));
                     }

                     return this.ordersService.getOrderWithSpend(orderId);
                   }),
                   switchMap((order: OrderDTO) => {
                     order.productID      =<number>this.selectedProductId;
                     order.updatedByUserId=userId;

                     return this.ordersService.save(order);
                   })
                 );

        case BulkSimAction.Suspend:
          return this.devicesService.suspend(iccid, userId);

        case BulkSimAction.Cease:
          return this.devicesService.deprovision(iccid, userId);

        case BulkSimAction.Throttle:
          return this.devicesService.throttle(iccid, this.selectedThrottleId, userId);

        case BulkSimAction.OverrideLimit:
          return this.ordersService.getOrderIdByIccid(iccid)
                 .pipe(
                   switchMap(idResult => {
                     const orderId: number=<number>(idResult?.result ?? -1);

                     if (orderId<=0) {
                       return throwError(() => new Error("BulkSimActionsModalFormComponent.executeForRow() - Order could not be found for ICCID"));
                     }

                     return this.ordersService.getOrderWithSpend(orderId);
                   }),
                   switchMap((order: OrderDTO) => {
                     this.applyOverrideLimitToOrder(order);
                     order.updatedByUserId=userId;

                     return this.ordersService.save(order);
                   }),
                   switchMap(() => this.devicesService.simLimitChanged(iccid, userId))
                 );

        case BulkSimAction.ChangeThisMonthLimit: {
          const isDataLimit          : boolean       =row.limitType===LimitType.Data;
          const monthLimitCurrent    : number | null =(!isDataLimit && this.overrideSpendCurrentMonth) ? this.monthSpendValue     : null;
          const monthDataLimitCurrent: number | null =( isDataLimit && this.overrideDataCurrentMonth)  ? this.monthDataValue      : null;
          const monthWeightCode      : string | null =( isDataLimit && this.overrideDataCurrentMonth)  ? this.monthDataWeightCode : null;

          return this.ordersService.getOrderIdByIccid(iccid)
                 .pipe(
                   switchMap(idResult => {
                     const orderId: number=<number>(idResult?.result ?? -1);

                     if (orderId<=0) {
                       return throwError(() => new Error("BulkSimActionsModalFormComponent.executeForRow() - Order could not be found for ICCID"));
                     }

                     return this.ordersService.putOrderCurrentMonthLimit(orderId, monthLimitCurrent, monthDataLimitCurrent, monthWeightCode, userId);
                   }),
                   switchMap(() => this.devicesService.simLimitChanged(iccid, userId))
                 );
        }

        default:
          return throwError(() => new Error("BulkSimActionsModalFormComponent.executeForRow() - Unknown action"));
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  private mapLimitType(limitTypeName: string): number {
    try {
      const name: string=(limitTypeName ?? "").toLowerCase();

      if (name===LimitTypeName.Spend.toLowerCase()) {
        return LimitType.Spend;
      }

      if (name===LimitTypeName.Data.toLowerCase()) {
        return LimitType.Data;
      }

      return LimitType.Off;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return LimitType.Off;
    }
  }

  private normaliseRows(rawRows: any[]): BulkSimRow[] {
    try {
      return (rawRows ?? []).map(row => ({
        companyId  : <number>(row?.companyID ?? row?.companyId ?? -1),
        companyName: <string>(row?.companyName ?? ""),
        iccid      : <string>(row?.iccid ?? row?.imeiIccid ?? ""),
        limitType  : this.mapLimitType(<string>(row?.orderSpendDTO?.limitTypeName ?? row?.limitTypeName ?? "")),
        userName   : <string>(row?.fullname ?? row?.fullName ?? row?.username ?? "")
      }));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  private loadCompanyLimits(): void {
    try {
      const companyId: number=<number>(this.selectedRows?.[0]?.companyId ?? -1);

      if (companyId<=0) {
        return;
      }

      this.companiesService.getById(companyId)
      .subscribe({
        next : (company: CompanyDTO) => { this.companyCurrencySymbol=company?.invoiceCurrencySymbol ?? "£"; },
        error: error                 => { this.exceptionsService.handleException(new Error(error)); }
      });

      this.companyUsageLimitsService.getForCompany(companyId)
      .subscribe({
        next : (limits: CompanyUsageLimitDTO) => {
          this.companyUsageLimits   =limits;
          this.companyDataLimit     =limits?.dataLimit               ?? "";
          this.companyDataWeightCode=limits?.dataLimitDataWeightCode  ?? "MB";
          this.companyUsageLimit    =limits?.limit                    ?? "";

          //RBW_20260805 - Default to a custom limit when the company has no default (mirrors the single form)
          this.useCustomSpendLimit=!this.companyUsageLimit;
          this.useCustomDataLimit =!this.companyDataLimit;
        },
        error: error => { this.exceptionsService.handleException(new Error(error)); }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private loadProductsForCompany(): void {
    try {
      const companyId: number=<number>(this.selectedRows?.[0]?.companyId ?? -1);

      if (companyId<=0) {
        return;
      }

      this.productsService.getAvailableIdAndDescription(-1, companyId)
      .subscribe({
        next : data  => { this.products=data ?? []; },
        error: error => { this.exceptionsService.handleException(new Error(error)); }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private openModal(): void {
    try {
      this.modalRef=this.modalService.open(this.bulkSimActionsModalForm, { backdrop: this.modalBackGround,
                                                                           centered: true,
                                                                           keyboard: false,
                                                                           size    : msSize.ExtraLarge });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private processNext(): void {
    try {
      if (this.currentIndex>=this.totalCount) {
        this.processing=false;
        this.step      ="complete";

        this.auditBulk("Bulk " + this.actionLabel + " completed — " + this.successCount + " succeeded, " + this.errorCount + " failed of " + this.totalCount + " connection(s)",
                       this.auditResultsInfo());

        return;
      }

      const row: BulkSimRow=this.runRows[this.currentIndex];
      let   succeeded: boolean=false;

      this.processingSub=this.executeForRow(row)
      .subscribe({
        next    : ()    => { succeeded=true; },
        error   : error => { this.recordResult(row, false, this.exceptionsService.formatHttpError(error)); this.advance(); },
        complete: ()    => { this.recordResult(row, succeeded, ""); this.advance(); }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private advance(): void {
    try {
      this.currentIndex++;
      this.progressPercent=this.totalCount>0 ? Math.round((this.currentIndex / this.totalCount) * 100) : 100;
      this.processNext();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private recordResult(row: BulkSimRow, ok: boolean, message: string): void {
    try {
      this.results.push({
        companyName: row?.companyName ?? "",
        iccid      : row?.iccid       ?? "",
        message    : message ?? "",
        ok         : ok,
        userName   : row?.userName    ?? ""
      });

      if (ok) {
        this.successCount++;
      } else {
        this.errorCount++;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private resetState(): void {
    try {
      this.companyCurrencySymbol    ="£";
      this.companyDataLimit         ="";
      this.companyDataWeightCode    ="MB";
      this.companyUsageLimit        ="";
      this.companyUsageLimits       =undefined;
      this.confirmSummary           ="";
      this.currentIndex             =0;
      this.dataWeightCode           ="MB";
      this.errorCount               =0;
      this.formSubmitted            =false;
      this.limitValue               =null;
      this.monthDataValue           =null;
      this.monthDataWeightCode      ="MB";
      this.monthSpendValue          =null;
      this.overrideDataCurrentMonth =true;
      this.overrideSpendCurrentMonth=true;
      this.processing               =false;
      this.products                 =[];
      this.progressPercent          =0;
      this.results                  =[];
      this.runRows                  =[];
      this.selectedLimitType        =LimitType.Spend;
      this.selectedProductId        =null;
      this.selectedThrottleId       =ThrottleType.Throttle64k;
      this.successCount             =0;
      this.useCustomDataLimit       =true;
      this.useCustomSpendLimit      =true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Public Methods

  public cancelForm(): void {
    try {
      this.processingSub?.unsubscribe();
      this.modalRef?.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public canProceedConfigure(): boolean {
    try {
      if (this.action===BulkSimAction.ChangeProduct) {
        return this.selectedProductId != null && <number>this.selectedProductId>0;
      }

      if (this.action===BulkSimAction.ChangeThisMonthLimit) {
        if (!this.hasSpendSims && !this.hasDataSims) {
          return false;
        }

        let ok: boolean=true;

        if (this.hasSpendSims && this.overrideSpendCurrentMonth) {
          ok=ok && this.monthSpendValue != null && <number>this.monthSpendValue>=0;
        }

        if (this.hasDataSims && this.overrideDataCurrentMonth) {
          ok=ok && this.monthDataValue != null && <number>this.monthDataValue>=0;
        }

        return ok;
      }

      if (this.isOverrideLimit) {
        if (this.selectedLimitType===LimitType.Off) {
          return true;
        }

        if (this.selectedLimitType===LimitType.Spend && !this.useCustomSpendLimit) {
          return true;
        }

        if (this.selectedLimitType===LimitType.Data && !this.useCustomDataLimit) {
          return true;
        }

        return this.limitValue != null && <number>this.limitValue>=0;
      }

      return true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public selectLimitType(limitType: number): void {
    try {
      this.selectedLimitType=limitType;
      this.formSubmitted    =false;

      if (limitType===LimitType.Spend) {
        this.useCustomSpendLimit=!this.companyUsageLimit;
      } else if (limitType===LimitType.Data) {
        this.useCustomDataLimit=!this.companyDataLimit;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onUseCustomSpendLimitChange(event: Event): void {
    try {
      this.useCustomSpendLimit=(<HTMLInputElement>event.target).checked;

      if (!this.useCustomSpendLimit) {
        this.limitValue=null;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onUseCustomDataLimitChange(event: Event): void {
    try {
      this.useCustomDataLimit=(<HTMLInputElement>event.target).checked;

      if (!this.useCustomDataLimit) {
        this.limitValue=null;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onOverrideSpendCurrentMonthChange(event: Event): void {
    try {
      this.overrideSpendCurrentMonth=(<HTMLInputElement>event.target).checked;

      if (!this.overrideSpendCurrentMonth) {
        this.monthSpendValue=null;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onOverrideDataCurrentMonthChange(event: Event): void {
    try {
      this.overrideDataCurrentMonth=(<HTMLInputElement>event.target).checked;

      if (!this.overrideDataCurrentMonth) {
        this.monthDataValue=null;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public confirm(): void {
    try {
      if (this.totalCount<=0) {
        return;
      }

      this.currentIndex   =0;
      this.errorCount     =0;
      this.progressPercent=0;
      this.results        =[];
      this.successCount   =0;
      this.processing     =true;
      this.step           ="progress";

      this.auditBulk("Bulk " + this.actionLabel + " started — " + this.totalCount + " connection(s)",
                     "ICCIDs: " + this.iccidListOrNone(this.runRows.map(x => x.iccid)));

      this.processNext();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public finishAndClose(): void {
    try {
      this.completed.emit();
      this.modalRef?.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public next(): void {
    try {
      this.formSubmitted=true;

      if (!this.canProceedConfigure()) {
        return;
      }

      this.buildConfirmSummary();
      this.step="confirm";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public openForm(action: BulkSimAction, rawRows: any[]): void {
    try {
      if (rawRows==undefined || rawRows.length<=0) {
        throw new Error("BulkSimActionsModalFormComponent.openForm() - No connections were selected");
      }

      this.resetState();

      this.action      =action;
      this.selectedRows=this.normaliseRows(rawRows);
      this.runRows     =action===BulkSimAction.ChangeThisMonthLimit
                        ? this.selectedRows.filter(x => x.limitType===LimitType.Spend || x.limitType===LimitType.Data)
                        : this.selectedRows;
      this.totalCount  =this.runRows.length;
      this.step        =this.needsConfigure ? "configure" : "confirm";

      if (this.action===BulkSimAction.ChangeProduct) {
        this.loadProductsForCompany();
      }

      if (this.isLimitAction) {
        this.loadCompanyLimits();
      }

      if (!this.needsConfigure) {
        this.buildConfirmSummary();
      }

      this.openModal();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
